// Демо-поездка первого запуска: Нью-Йорк — Рим, две недели, тремя способами.
// Не импортирует src/core.
//
// Данные подобраны так, чтобы гость увидел смысл инструмента, ничего не нажимая:
// три варианта одной поездки различаются не ценой на пару долларов, а самим
// устройством маршрута — где живёшь по дороге, сколько времени в воздухе, чем
// платишь за прямой рейс. Ответ на вопрос «что дешевле» здесь неочевиден, и
// именно поэтому варианты хочется сравнить.
//
// Аэропорты — самые известные (JFK, LHR, FCO, CDG): маршрут должен читаться
// с одного взгляда, без сверки кодов со справочником.
//
// Всё на английском, включая города и подписи расходов: демо видит любой
// посетитель главной, а язык интерфейса ему подставляется свой.
import type {
  AppState,
  BaggageSegment,
  FlightSegment,
  LodgingSegment,
  Segment,
  SimpleCostSegment,
  TransferSegment,
  TripVariant,
} from '../types';
import { DEFAULT_CURRENCIES } from '../types';
import { VARIANT_COLORS } from './colors';

/**
 * Пустая поездка: библиотека и варианты чистые, справочник валют — по
 * умолчанию. С неё начинается каждая новая поездка: демо уместно ровно один
 * раз, при первом запуске, а не всякий раз, когда человек заводит следующий
 * маршрут.
 */
export function emptyState(): AppState {
  return {
    library: {},
    variants: [],
    travelers: 1,
    displayCurrency: 'USD',
    currencies: DEFAULT_CURRENCIES.map((c) => ({ ...c })),
  };
}

/**
 * Возвращает НОВЫЙ объект AppState при каждом вызове — никакого шаринга
 * ссылок между вызовами (каждый объект/массив создаётся заново).
 */
export function seedState(): AppState {
  // ── Вариант «London» — туда через Лондон, обратно через Париж ─────────────
  const flightJfkLhr: FlightSegment = {
    kind: 'flight',
    id: 'f_jfk_lhr',
    from: 'JFK',
    to: 'LHR',
    fromTz: 'America/New_York',
    toTz: 'Europe/London',
    departLocal: '2026-10-02T19:30',
    arriveLocal: '2026-10-03T07:40',
    carrier: 'British Airways',
    price: { amount: 42000, currency: 'USD' },
    priceScope: 'one_way',
  };

  const lodgingLondon: LodgingSegment = {
    kind: 'lodging',
    id: 'l_london',
    city: 'London',
    nights: 2,
    pricePerNight: { amount: 18000, currency: 'USD' },
    wholeStay: true,
  };

  const flightLhrFco: FlightSegment = {
    kind: 'flight',
    id: 'f_lhr_fco',
    from: 'LHR',
    to: 'FCO',
    fromTz: 'Europe/London',
    toTz: 'Europe/Rome',
    departLocal: '2026-10-05T06:50',
    arriveLocal: '2026-10-05T10:10',
    carrier: 'British Airways',
    price: { amount: 9500, currency: 'USD' },
    priceScope: 'one_way',
  };

  // Две недели в Риме — общая часть всех трёх маршрутов. Один блок на все
  // варианты, а не три копии: в этом и смысл библиотеки.
  const lodgingRome: LodgingSegment = {
    kind: 'lodging',
    id: 'l_rome',
    city: 'Rome',
    nights: 14,
    pricePerNight: { amount: 13000, currency: 'USD' },
    // «На весь промежуток»: ночи считаются от прилёта до следующего вылета
    // сами. Иначе один блок на три маршрута врал бы в двух из них — стоянки
    // в них разной длины, а блок один.
    wholeStay: true,
  };

  const flightFcoCdg: FlightSegment = {
    kind: 'flight',
    id: 'f_fco_cdg',
    from: 'FCO',
    to: 'CDG',
    fromTz: 'Europe/Rome',
    toTz: 'Europe/Paris',
    departLocal: '2026-10-19T09:40',
    arriveLocal: '2026-10-19T11:55',
    carrier: 'Air France',
    price: { amount: 11000, currency: 'USD' },
    priceScope: 'one_way',
  };

  // Переезд из аэропорта в город: цена за поездку, а не за человека, — на
  // четверых путешественников это сразу видно в сравнении.
  const transferCdgParis: TransferSegment = {
    kind: 'transfer',
    id: 't_cdg_paris',
    from: 'CDG',
    to: 'Paris',
    durationMin: 50,
    price: { amount: 1300, currency: 'USD' },
    perPerson: false,
  };

  const lodgingParis: LodgingSegment = {
    kind: 'lodging',
    id: 'l_paris',
    city: 'Paris',
    nights: 1,
    pricePerNight: { amount: 21000, currency: 'USD' },
    wholeStay: true,
  };

  const flightCdgJfk: FlightSegment = {
    kind: 'flight',
    id: 'f_cdg_jfk',
    from: 'CDG',
    to: 'JFK',
    fromTz: 'Europe/Paris',
    toTz: 'America/New_York',
    departLocal: '2026-10-20T10:55',
    arriveLocal: '2026-10-20T13:20',
    carrier: 'Air France',
    price: { amount: 46000, currency: 'USD' },
    priceScope: 'one_way',
  };

  // ── Вариант «Paris» — зеркальный: туда через Париж, обратно через Лондон ──
  const flightJfkCdg: FlightSegment = {
    kind: 'flight',
    id: 'f_jfk_cdg',
    from: 'JFK',
    to: 'CDG',
    fromTz: 'America/New_York',
    toTz: 'Europe/Paris',
    departLocal: '2026-10-02T22:00',
    arriveLocal: '2026-10-03T11:30',
    carrier: 'Delta',
    price: { amount: 39500, currency: 'USD' },
    priceScope: 'one_way',
  };

  const flightCdgFco: FlightSegment = {
    kind: 'flight',
    id: 'f_cdg_fco',
    from: 'CDG',
    to: 'FCO',
    fromTz: 'Europe/Paris',
    toTz: 'Europe/Rome',
    departLocal: '2026-10-05T10:05',
    arriveLocal: '2026-10-05T12:15',
    carrier: 'ITA Airways',
    price: { amount: 8500, currency: 'USD' },
    priceScope: 'one_way',
  };

  const flightFcoLhr: FlightSegment = {
    kind: 'flight',
    id: 'f_fco_lhr',
    from: 'FCO',
    to: 'LHR',
    fromTz: 'Europe/Rome',
    toTz: 'Europe/London',
    departLocal: '2026-10-19T11:40',
    arriveLocal: '2026-10-19T13:35',
    carrier: 'British Airways',
    price: { amount: 10500, currency: 'USD' },
    priceScope: 'one_way',
  };

  // Стыковка в Хитроу — 1 час 25 минут при пороге в 90: гость сразу видит
  // жёлтое предупреждение о рискованной пересадке, ничего для этого не делая.
  const flightLhrJfk: FlightSegment = {
    kind: 'flight',
    id: 'f_lhr_jfk',
    from: 'LHR',
    to: 'JFK',
    fromTz: 'Europe/London',
    toTz: 'America/New_York',
    departLocal: '2026-10-19T15:00',
    arriveLocal: '2026-10-19T18:05',
    carrier: 'British Airways',
    price: { amount: 44500, currency: 'USD' },
    priceScope: 'one_way',
  };

  // ── Вариант «Nonstop» — прямой в обе стороны, цена за пару плеч сразу ─────
  const flightJfkFco: FlightSegment = {
    kind: 'flight',
    id: 'f_jfk_fco',
    from: 'JFK',
    to: 'FCO',
    fromTz: 'America/New_York',
    toTz: 'Europe/Rome',
    departLocal: '2026-10-04T18:55',
    arriveLocal: '2026-10-05T09:20',
    carrier: 'Delta',
    price: { amount: 154000, currency: 'USD' },
    priceScope: 'round_trip_full',
    linkedTo: 'f_fco_jfk',
  };

  const flightFcoJfk: FlightSegment = {
    kind: 'flight',
    id: 'f_fco_jfk',
    from: 'FCO',
    to: 'JFK',
    fromTz: 'Europe/Rome',
    toTz: 'America/New_York',
    departLocal: '2026-10-19T08:45',
    arriveLocal: '2026-10-19T13:15',
    carrier: 'Delta',
    price: { amount: 0, currency: 'USD' },
    priceScope: 'round_trip_full',
    linkedTo: 'f_jfk_fco',
  };

  // ── Запасные блоки: лежат в библиотеке, ни в один вариант не входят ───────
  // Библиотека — черновик, а не список покупок: часть найденного всегда
  // остаётся в стороне. Демо показывает это состояние, а не идеальный порядок.
  const flightJfkFraSpare: FlightSegment = {
    kind: 'flight',
    id: 'f_jfk_fra',
    from: 'JFK',
    to: 'FRA',
    fromTz: 'America/New_York',
    toTz: 'Europe/Berlin',
    departLocal: '2026-10-02T17:25',
    arriveLocal: '2026-10-03T07:15',
    carrier: 'Lufthansa',
    price: { amount: 38500, currency: 'USD' },
    priceScope: 'one_way',
  };

  const lodgingRomeCheaper: LodgingSegment = {
    kind: 'lodging',
    id: 'l_rome_hostel',
    city: 'Rome',
    nights: 14,
    pricePerNight: { amount: 7500, currency: 'USD' },
  };

  const baggageChecked: BaggageSegment = {
    kind: 'baggage',
    id: 'b_checked_bag',
    flightId: 'f_jfk_lhr',
    label: '23kg checked bag',
    price: { amount: 6500, currency: 'USD' },
  };

  const costTaxi: SimpleCostSegment = {
    kind: 'taxi',
    id: 'c_taxi',
    label: 'JFK taxi to Manhattan',
    price: { amount: 7000, currency: 'USD' },
    quantity: 1,
  };

  // Еда считается на всю дорогу, а не на две недели в Риме: в маршрутах с
  // остановками едят и в Лондоне, и в Париже, и в день перелёта. Взят самый
  // длинный из трёх — 18 дней в пути; на коротких лишние дни убираются одним
  // движением, а вот недостающие пришлось бы замечать самому.
  const costFood: SimpleCostSegment = {
    kind: 'food',
    id: 'c_food',
    label: 'Food, per day',
    price: { amount: 4500, currency: 'USD' },
    quantity: 18,
  };

  const costInsurance: SimpleCostSegment = {
    kind: 'misc',
    id: 'c_insurance',
    label: 'Travel insurance',
    price: { amount: 6000, currency: 'USD' },
    quantity: 1,
  };

  const library: Record<string, Segment> = {
    [flightJfkLhr.id]: flightJfkLhr,
    [lodgingLondon.id]: lodgingLondon,
    [flightLhrFco.id]: flightLhrFco,
    [lodgingRome.id]: lodgingRome,
    [flightFcoCdg.id]: flightFcoCdg,
    [transferCdgParis.id]: transferCdgParis,
    [lodgingParis.id]: lodgingParis,
    [flightCdgJfk.id]: flightCdgJfk,
    [flightJfkCdg.id]: flightJfkCdg,
    [flightCdgFco.id]: flightCdgFco,
    [flightFcoLhr.id]: flightFcoLhr,
    [flightLhrJfk.id]: flightLhrJfk,
    [flightJfkFco.id]: flightJfkFco,
    [flightFcoJfk.id]: flightFcoJfk,
    [flightJfkFraSpare.id]: flightJfkFraSpare,
    [lodgingRomeCheaper.id]: lodgingRomeCheaper,
    [baggageChecked.id]: baggageChecked,
    [costTaxi.id]: costTaxi,
    [costFood.id]: costFood,
    [costInsurance.id]: costInsurance,
  };

  const variantLondon: TripVariant = {
    id: 'v_london',
    name: 'London',
    color: VARIANT_COLORS[0] ?? '#5b7c99',
    segmentIds: [
      flightJfkLhr.id,
      lodgingLondon.id,
      flightLhrFco.id,
      lodgingRome.id,
      flightFcoCdg.id,
      transferCdgParis.id,
      lodgingParis.id,
      flightCdgJfk.id,
    ],
  };

  const variantParis: TripVariant = {
    id: 'v_paris',
    name: 'Paris',
    color: VARIANT_COLORS[1] ?? '#6b8f71',
    segmentIds: [
      flightJfkCdg.id,
      lodgingParis.id,
      flightCdgFco.id,
      lodgingRome.id,
      flightFcoLhr.id,
      flightLhrJfk.id,
    ],
  };

  const variantNonstop: TripVariant = {
    id: 'v_nonstop',
    name: 'Nonstop',
    color: VARIANT_COLORS[2] ?? '#8f6b5b',
    segmentIds: [flightJfkFco.id, lodgingRome.id, flightFcoJfk.id],
  };

  return {
    library,
    variants: [variantLondon, variantParis, variantNonstop],
    travelers: 1,
    displayCurrency: 'USD',
    currencies: DEFAULT_CURRENCIES.map((c) => ({ ...c })),
  };
}
